<script setup>
import { ref } from 'vue';
import IconChevronDown from '../../icons/IconChevronDown.vue';
import IconFlag from '../../icons/IconFlag.vue';
const { options, name, id } = defineProps({
  options: {
    type: Array
  },
  name: String,
  id: String,
  disabled: {
    type: Boolean,
    default: false,
  },
  flags: {
    type: Boolean,
    default: false,
  }
});

const selectedOption = ref(options[0]);
const expanded = ref(false);
const button = ref(null);
const dropdownTop = ref(false);

const emit = defineEmits(['change']);

function select(event) {
  expanded.value = false;
  emit('change', { target: { id, value: event.target.value } });
}

function toggle() {
  if(button.value && !expanded.value) {
    const {top, height} = button.value.getBoundingClientRect();
    dropdownTop.value = top > window.innerHeight - top - height;
  }
  expanded.value = !expanded.value;
}

function close() {
  expanded.value = false;
}
</script>

<template>
  <div class="select" :id="id" v-click-outside="close">
    <button :aria-expanded="expanded" :disabled="disabled" @click="toggle" ref="button" class="select__button">
      <div class="select__button-text">
        <span class="select__label font-l4" v-if="name">{{ name }}</span>
        <span class="select__value">
          <IconFlag v-if="flags" :code="selectedOption.toLowerCase()" class="select__flag" />
          {{ selectedOption }}
        </span>
      </div>
      <IconChevronDown class="select__icon" />
    </button>
    <div class="select__overlay" :class="{'select__overlay--top': dropdownTop}" v-show="expanded">
      <label v-for="option of options" :key="option" :class="{'selected': option === selectedOption}">
        <input type="radio" :value="option" v-model="selectedOption" @click="select" />
        <IconFlag v-if="flags" :code="option.toLowerCase()" class="select__flag" />
        {{ option }}
      </label>
    </div>
  </div>
</template>

<style scoped lang="scss">
.select {
  position: relative;

  &__button {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 12px 16px;
    border-radius: 20px;
    border: 1px solid var(--clr-gray-200);
    min-height: 56px;
    min-width: max-content;

    &[aria-expanded="true"] {
      border-color: var(--clr-blue-700);

      .select__icon {
        transform: rotate(180deg);
      }
    }

    &:disabled {
      background: var(--clr-gray-50);
      color: var(--clr-gray-300);
    }
  }

  &__button-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  &__label {
    color: var(--clr-brand-300);
    line-height: 1;
  }

  &__value {
    font-weight: 600;
    text-overflow: ellipsis;

    display: inline-flex;
    gap: 10px;
    align-items: center;
  }

  &__icon {
    transition: transform .2s;
    transform-origin: 50% 45%;
  }

  &__overlay {
    display: flex;
    position: absolute;
    z-index: 1;
    width: 100%;
    margin-block: 8px;
    top: 100%;

    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    border-radius: 20px;
    background: var(--clr-white);
    /* Hover Shadow */
    box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.05), 0px 5px 15px 0px rgba(0, 0, 0, 0.10);
    overflow: hidden;
    max-height: 220px;
    overflow-y: auto;

    &--top {
      top: unset;
      bottom: 100%;
    }

    input {
      display: none;
    }

    label {
      display: flex;
      height: 48px;
      padding: 12px 16px;
      align-items: center;
      gap: 10px;
      align-self: stretch;
      font-weight: 600;
      line-height: 1.5;

      &.selected {
        background: var(--clr-blue-100);
      }
    }
  }

  &__flag {
    border-radius: 4px;
    flex-shrink: 0;
  }
}

.dropdown {
  border-bottom: 1px solid var(--clr-gray-50);

  &-checkbox {
    display: none;

    &:checked {
      & ~ .dropdown-content {
        grid-template-rows: 1fr;
      }

      & ~ .dropdown-header > .dropdown-icon {
        transform: rotate(180deg);
      }
    }
  }

  &-icon {
    transition: transform 0.4s ease-in-out;
  }

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 24px 16px;

    h2 {
      font-size: 1rem;
      font-style: normal;
      font-weight: 600;
      line-height: 1.5;
    }
  }

  &-content {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.4s ease-in-out;
    font-size: 0.875rem;
    padding-inline: 16px;

    & > div {
      overflow: hidden;
    }

    p {
      margin-bottom: 16px;
    }
  }
}
</style>
